import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import type { LucideIcon } from "lucide-react";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpToLine,
  Check,
  Eye,
  FolderUp,
  Globe,
  ImageUp,
  MailCheck,
  ScanFace,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  UploadCloud,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { fill } from "@/content/pricing";
import { PREVIEW_FIRST_PATH, usePreviewFirstCopy } from "@/content/previewFirst";
import { WEB_UPLOADER_PATH } from "@/content/webUploader";

// "How preview-first uploads work" at photohawk.com/preview-first-uploads: public and indexable,
// linked from the web uploader (the "New" pill in its header, the go-live strip and the preview-first
// details) and from the footer. Copy and the code it's backed by: src/content/previewFirst/copy.<lang>.ts.

/** The full-screen web uploader (sign-in first if needed); also the root of upload.<domain>. */
const UPLOADER_PATH = "/upload";
const CANONICAL = `https://photohawk.com${PREVIEW_FIRST_PATH}`;

const STEP_ICONS: Record<string, LucideIcon> = {
  add: FolderUp,
  previews: ImageUp,
  live: Globe,
  full: UploadCloud,
};

const BUYER_ICONS: Record<string, LucideIcon> = {
  protected: ShieldCheck,
  search: ScanFace,
  delivery: MailCheck,
};

/** Where the previews finish (and the gallery can go live) on the example timeline, in %. */
const LIVE_AT = 24;

/** The "gallery live" tick across a timeline bar. */
const LiveMarker = () => (
  <span
    className="pointer-events-none absolute -bottom-2 -top-2 z-10 border-l-2 border-dashed border-emerald-600 dark:border-emerald-400"
    style={{ left: `${LIVE_AT}%` }}
    aria-hidden="true"
  />
);

const PreviewFirstUploads = () => {
  const { copy } = usePreviewFirstCopy();
  const { hero, steps, buyers, strip, faq, cta } = copy;
  const t = steps.timeline;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{copy.seo.title}</title>
        <meta name="description" content={copy.seo.description} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={copy.seo.title} />
        <meta property="og:description" content={copy.seo.description} />
      </Helmet>
      <AppHeader variant="marketing" />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-hawk-navy pb-16 pt-32 text-white">
          <div className="absolute inset-0 bg-radial-orange opacity-20" />
          <div className="container relative z-10 mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-5 flex flex-wrap items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hawk-orange to-hawk-gold px-3 py-1 text-sm font-bold uppercase tracking-wide text-white shadow-glow">
                  <Sparkles size={14} aria-hidden="true" />
                  {hero.badge}
                </span>
                <span className="text-sm font-medium text-white/80">{hero.eyebrow}</span>
              </div>
              <h1 className="mb-6 text-4xl font-display font-bold md:text-5xl">
                {hero.title} <span className="text-gradient-primary">{hero.titleHighlight}</span>
              </h1>
              <p className="mx-auto mb-8 max-w-2xl text-lg text-white/80">{hero.subtitle}</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button variant="hero" size="lg" asChild>
                  <Link to={UPLOADER_PATH}>
                    {hero.ctaPrimary}
                    <ArrowRight />
                  </Link>
                </Button>
                <Button variant="heroOutline" size="lg" asChild>
                  <a href="#how">
                    {hero.ctaSecondary}
                    <ArrowDown />
                  </a>
                </Button>
              </div>
              <Link
                to={WEB_UPLOADER_PATH}
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-hawk-gold underline-offset-4 hover:underline"
              >
                {hero.uploaderLink}
                <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </section>

        {/* How it works: four steps + an example timeline */}
        <section id="how" className="scroll-mt-24 py-20" aria-labelledby="how-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto mb-12 max-w-2xl text-center">
              <h2 id="how-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">
                {steps.heading}
              </h2>
              <p className="text-muted-foreground">{steps.subtitle}</p>
            </div>

            <ol className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {steps.items.map((step, i) => {
                const Icon = STEP_ICONS[step.id] ?? Check;
                const last = i === steps.items.length - 1;
                return (
                  <li key={step.id} className="relative rounded-2xl bg-card p-6 shadow-lg">
                    <div className="mb-4 flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-hawk-orange to-hawk-gold">
                        <Icon className="text-white" size={22} aria-hidden="true" />
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wide text-hawk-orange">
                        {fill(steps.stepLabel, { n: i + 1 })}
                      </span>
                    </div>
                    <h3 className="mb-1 font-display text-lg font-bold">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.body}</p>
                    {!last && (
                      <span
                        className="absolute -right-5 top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background text-hawk-orange shadow lg:flex"
                        aria-hidden="true"
                      >
                        <ArrowRight size={16} />
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>

            {/* Example timeline: the previews finish early and the gallery goes live; the full-quality
                files carry on behind. Illustrative only, so no figures beyond "minutes". */}
            <figure className="mx-auto mt-12 max-w-4xl rounded-2xl border border-border bg-card p-5 shadow-lg sm:p-8">
              <figcaption className="mb-6 text-sm font-semibold text-muted-foreground">{t.label}</figcaption>
              <div className="space-y-6">
                <div>
                  <div className="mb-2 flex items-baseline justify-between gap-2 text-sm">
                    <span className="font-semibold">{t.previews}</span>
                    <span className="text-muted-foreground">{t.previewsTime}</span>
                  </div>
                  <div className="relative h-3 rounded-full bg-secondary">
                    <LiveMarker />
                    <div
                      className="h-3 rounded-full bg-gradient-to-r from-hawk-orange to-hawk-gold"
                      style={{ width: `${LIVE_AT}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-2 flex items-baseline justify-between gap-2 text-sm">
                    <span className="font-semibold">{t.full}</span>
                    <span className="text-muted-foreground">{t.fullTime}</span>
                  </div>
                  <div className="relative h-3 rounded-full bg-secondary">
                    <LiveMarker />
                    <div
                      className="h-3 rounded-full bg-hawk-navy/70 dark:bg-hawk-slate"
                      style={{
                        width: "100%",
                        backgroundImage:
                          "repeating-linear-gradient(-45deg, transparent 0 6px, hsl(0 0% 100% / 0.18) 6px 12px)",
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="relative mt-4 h-7">
                <span
                  className="absolute inline-flex -translate-x-4 items-center gap-1.5 whitespace-nowrap rounded-full bg-emerald-600/10 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400"
                  style={{ left: `${LIVE_AT}%` }}
                >
                  <Globe size={13} aria-hidden="true" />
                  {t.live}
                </span>
              </div>
            </figure>
          </div>
        </section>

        {/* What buyers see */}
        <section className="bg-secondary/30 py-20" aria-labelledby="buyers-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto mb-12 max-w-2xl text-center">
              <h2 id="buyers-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">
                {buyers.heading}
              </h2>
              <p className="text-muted-foreground">{buyers.subtitle}</p>
            </div>
            <ul className="mx-auto grid max-w-6xl gap-6 md:grid-cols-3">
              {buyers.items.map((item) => {
                const Icon = BUYER_ICONS[item.id] ?? Check;
                return (
                  <li key={item.id} className="flex gap-4 rounded-2xl border border-border bg-card p-6">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-hawk-orange/10">
                      <Icon size={18} className="text-hawk-orange" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="mb-1 font-semibold">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.body}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* The go-live strip in the uploader, with an example */}
        <section className="py-20" aria-labelledby="strip-heading">
          <div className="container mx-auto grid max-w-6xl items-center gap-10 px-4 lg:grid-cols-2">
            <div>
              <h2 id="strip-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">
                {strip.heading}
              </h2>
              <p className="text-muted-foreground">{strip.body}</p>
            </div>
            <figure className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
              <figcaption className="border-b border-border px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {strip.exampleLabel}
              </figcaption>
              <ul className="flex flex-col gap-2 bg-primary/5 px-4 py-3 text-sm">
                <li className="flex items-center gap-2 font-medium text-emerald-700 dark:text-emerald-400">
                  <Globe size={15} aria-hidden="true" /> {strip.live}
                </li>
                <li className="flex items-center gap-2">
                  <ShoppingCart size={15} className="text-muted-foreground" aria-hidden="true" /> {strip.orders}
                </li>
                <li className="flex items-center gap-2">
                  <Eye size={15} className="text-muted-foreground" aria-hidden="true" /> {strip.visitors}
                </li>
                <li className="pl-[23px] text-muted-foreground">{strip.left}</li>
                <li className="flex items-center gap-2 font-medium">
                  <ArrowUpToLine size={15} className="text-primary" aria-hidden="true" /> {strip.jumped}
                </li>
              </ul>
            </figure>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-secondary/30 py-20" aria-labelledby="faq-heading">
          <div className="container mx-auto max-w-3xl px-4">
            <h2 id="faq-heading" className="mb-8 text-center text-3xl font-display font-bold md:text-4xl">
              {faq.heading}
            </h2>
            <Accordion type="single" collapsible className="rounded-2xl bg-card px-6 shadow-lg">
              {faq.items.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`} className={i === faq.items.length - 1 ? "border-b-0" : undefined}>
                  <AccordionTrigger className="text-left text-base">{item.q}</AccordionTrigger>
                  <AccordionContent forceMount className="text-muted-foreground">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden bg-hawk-navy py-20 text-white">
          <div className="absolute inset-0 bg-radial-orange opacity-20" />
          <div className="container relative z-10 mx-auto max-w-3xl px-4 text-center">
            <UploadCloud className="mx-auto mb-4 text-hawk-orange" size={32} aria-hidden="true" />
            <h2 className="mb-3 text-3xl font-display font-bold md:text-4xl">{cta.heading}</h2>
            <p className="mb-8 text-white/80">{cta.body}</p>
            <Button variant="hero" size="lg" asChild>
              <Link to={UPLOADER_PATH}>
                {cta.button}
                <ArrowRight />
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PreviewFirstUploads;
